<script setup lang="ts">
	import convertToSpanishOrdinal from '@/helpers/convertToSpanishOrdinal'
	import { computed } from 'vue'

	import { Company } from '@/modules/companies/interfaces/company'
	import { RateForm } from '@/modules/hotels/interfaces/rateForm'
	import { InputTypes } from '@/modules/hotels/interfaces/rateInputTypes'
	import { User } from '@/modules/users/interfaces/user'

	import CompanyHotelLoyaltySelect from './CompanyHotelLoyaltySelect.vue'
	import CostCenterSelect from './CostCenterSelect.vue'

	interface Props {
		guests: number
		travelers: number
		form: RateForm
		attemptedToSubmit: boolean
		inputTypes: InputTypes
		users: User[]
		company?: Company
		hotelCode: string
		isCompanyRate: boolean
		provider?: 'amadeus' | 'hotelbeds'
		markGuestDirty?: (key: string) => void
		isGuestDirty?: (key: string) => boolean
		validate: (
			value: any,
			field: string,
			checkInvalidCharacters?: boolean
		) => string | undefined
	}

	interface Emits {
		(event: 'submit'): void
		(event: 'mandatory-change', value: boolean): void
	}

	const props = defineProps<Props>()

	const handleChange = (costCenter: number | null, travelerIndex: number) => {
		props.form.travelers.forEach((traveler, index) => {
			if (index === travelerIndex) {
				traveler.costCenter = costCenter
			}
		})
	}

	const emit = defineEmits<Emits>()

	const requiresCostCenter = computed(() =>
		Boolean(props.company?.mandatoryCostCenter)
	)
</script>

<template>
	<div class="space-y-6">
		<div
			v-for="(_, travelerIndex) in travelers"
			:key="travelerIndex"
			class="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300"
		>
			<!-- Room header -->
			<div
				v-if="travelers > 1"
				class="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200"
			>
				<div class="flex items-center gap-3">
					<div
						class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"
					>
						<svg
							class="w-5 h-5 text-blue-600"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h4M9 7h6m-6 4h6m-6 4h6"
							></path>
						</svg>
					</div>
					<div>
						<h3 class="font-inter font-semibold text-lg text-gray-900">
							Habitación #{{ travelerIndex + 1 }}
						</h3>
						<p class="font-inter text-sm text-gray-600">
							Complete la información de los huéspedes
						</p>
					</div>
				</div>
			</div>

			<div class="p-6">
				<form
					id="reservation-form"
					class="space-y-8"
					@submit.prevent="$emit('submit')"
				>
					<!-- Main Guest Section -->
					<div class="space-y-6">
						<!-- Section header -->
						<div class="flex items-center gap-3 pb-4 border-b border-gray-100">
							<div
								class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center"
							>
								<svg
									class="w-4 h-4 text-blue-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
									></path>
								</svg>
							</div>
							<div>
								<h4 class="font-inter font-semibold text-gray-900">
									Huésped titular
								</h4>
								<p class="font-inter text-sm text-gray-600">
									Información del huésped principal
								</p>
							</div>
						</div>

						<!-- Main guest form -->
						<div class="grid grid-cols-1 md:grid-cols-2 gap-6">
							<!-- First name -->
							<div class="space-y-2">
								<label
									:for="`name${travelerIndex}`"
									class="block font-inter text-sm font-medium text-gray-700"
								>
									Nombre(s)
								</label>
								<div class="relative">
									<input
										:id="`name${travelerIndex}`"
										:value="form.travelers[travelerIndex].mainGuest.name"
										type="text"
										disabled
										class="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 font-inter focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100"
										placeholder="Nombre del huésped titular"
									/>
									<div
										class="absolute inset-y-0 right-0 flex items-center pr-3"
									>
										<svg
											class="w-4 h-4 text-gray-400"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
											></path>
										</svg>
									</div>
								</div>
								<span
									v-if="attemptedToSubmit"
									class="text-sm text-red-500 font-inter"
								>
									{{
										validate(
											form.travelers[travelerIndex].mainGuest.name,
											inputTypes.name,
											true
										)
									}}
								</span>
							</div>

							<!-- Last name -->
							<div class="space-y-2">
								<label
									:for="`lastname${travelerIndex}`"
									class="block font-inter text-sm font-medium text-gray-700"
								>
									Apellido(s)
								</label>
								<div class="relative">
									<input
										:id="`lastname${travelerIndex}`"
										:value="form.travelers[travelerIndex].mainGuest.lastname"
										type="text"
										disabled
										class="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 font-inter focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100"
										placeholder="Apellido del huésped titular"
									/>
									<div
										class="absolute inset-y-0 right-0 flex items-center pr-3"
									>
										<svg
											class="w-4 h-4 text-gray-400"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
											></path>
										</svg>
									</div>
								</div>
								<span
									v-if="attemptedToSubmit"
									class="text-sm text-red-500 font-inter"
								>
									{{
										validate(
											form.travelers[travelerIndex].mainGuest.lastname,
											inputTypes.lastname,
											true
										)
									}}
								</span>
							</div>
						</div>

						<!-- El departamento pertenece al perfil del viajero; aquí sólo se
						muestra. El cliente elige únicamente uno de sus centros de costos. -->
						<div>
							<div
								v-if="users?.[travelerIndex]"
								class="space-y-2"
							>
								<CostCenterSelect
									:user="users[travelerIndex]"
									:attempted-to-submit="attemptedToSubmit"
									:required="requiresCostCenter"
									:cost-center="
										form.travelers[travelerIndex].costCenter ?? undefined
									"
									@update:cost-center="
										handleChange($event || null, travelerIndex)
									"
									@mandatory-change="emit('mandatory-change', $event)"
								/>
							</div>
						</div>
						<div
							v-if="company?.id"
							class="min-w-0 md:col-span-2"
						>
							<CompanyHotelLoyaltySelect
								:hotel-code="hotelCode"
								:provider="provider"
								:is-company-rate="isCompanyRate"
								:traveler-id="
									users[travelerIndex]?.id ??
									form.travelers[travelerIndex].mainGuest.id
								"
								:company-id="company.id"
								:model-value="form.travelers[travelerIndex].loyaltyProgram"
								@update:model-value="
									form.travelers[travelerIndex].loyaltyProgram = $event
								"
							/>
						</div>
					</div>

					<!-- Additional Guests Section -->
					<div
						v-if="Number(guests) > 1"
						class="space-y-6"
					>
						<!-- Section header -->
						<div class="flex items-center gap-3 pb-4 border-b border-gray-100">
							<div
								class="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center"
							>
								<svg
									class="w-4 h-4 text-green-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
									></path>
								</svg>
							</div>
							<div>
								<h4 class="font-inter font-semibold text-gray-900">
									Huéspedes adicionales
								</h4>
								<p class="font-inter text-sm text-gray-600">
									Información de los huéspedes que acompañan
								</p>
							</div>
						</div>

						<!-- Additional guests forms -->
						<div class="space-y-6">
							<div
								v-for="(guest, guestIndex) in Number(guests - 1)"
								:key="guestIndex"
								class="bg-gray-50 rounded-lg p-4 border border-gray-200"
							>
								<!-- Guest number -->
								<div class="flex items-center gap-2 mb-4">
									<div
										class="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs font-inter font-bold"
									>
										{{ guestIndex + 1 }}
									</div>
									<h5 class="font-inter font-medium text-gray-900">
										{{ convertToSpanishOrdinal(Number(guest)) }} huésped
										adicional
									</h5>
								</div>

								<!-- Guest form -->
								<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
									<!-- Guest first name -->
									<div class="space-y-2">
										<label
											:for="`guest_name_${travelerIndex}_${guestIndex}`"
											class="block font-inter text-sm font-medium text-gray-700"
										>
											Nombre(s) <span class="text-red-500">*</span>
										</label>
										<input
											:id="`guest_name_${travelerIndex}_${guestIndex}`"
											v-model="
												form.travelers[travelerIndex].additionalGuests![
													guestIndex
												].name
											"
											type="text"
											class="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-900 font-inter focus:ring-2 focus:ring-green-500 focus:border-green-500"
											placeholder="Nombre del huésped adicional"
											@blur="
												markGuestDirty?.(
													`guest_name_${travelerIndex}_${guestIndex}`
												)
											"
										/>
										<span
											v-if="
												isGuestDirty ?
													isGuestDirty(
														`guest_name_${travelerIndex}_${guestIndex}`
													)
												:	attemptedToSubmit
											"
											class="text-sm text-red-500 font-inter"
										>
											{{
												validate(
													form.travelers[travelerIndex].additionalGuests![
														guestIndex
													].name,
													inputTypes.name,
													true
												)
											}}
										</span>
									</div>

									<!-- Guest last name -->
									<div class="space-y-2">
										<label
											:for="`guest_lastname_${travelerIndex}_${guestIndex}`"
											class="block font-inter text-sm font-medium text-gray-700"
										>
											Apellido(s) <span class="text-red-500">*</span>
										</label>
										<input
											:id="`guest_lastname_${travelerIndex}_${guestIndex}`"
											v-model="
												form.travelers[travelerIndex].additionalGuests![
													guestIndex
												].lastname
											"
											type="text"
											class="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-900 font-inter focus:ring-2 focus:ring-green-500 focus:border-green-500"
											placeholder="Apellido del huésped adicional"
											@blur="
												markGuestDirty?.(
													`guest_lastname_${travelerIndex}_${guestIndex}`
												)
											"
										/>
										<span
											v-if="
												isGuestDirty ?
													isGuestDirty(
														`guest_lastname_${travelerIndex}_${guestIndex}`
													)
												:	attemptedToSubmit
											"
											class="text-sm text-red-500 font-inter"
										>
											{{
												validate(
													form.travelers[travelerIndex].additionalGuests![
														guestIndex
													].lastname,
													inputTypes.lastname,
													true
												)
											}}
										</span>
									</div>
								</div>
							</div>
						</div>

						<!-- Important notice -->
						<div class="bg-amber-50 border border-amber-200 rounded-lg p-4">
							<div class="flex gap-3">
								<svg
									class="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
									></path>
								</svg>
								<div>
									<h6 class="font-inter font-semibold text-amber-800">
										Información importante
									</h6>
									<p class="font-inter text-sm text-amber-700 mt-1">
										Los campos marcados con un asterisco (
										<span class="text-red-600 font-bold">*</span>) son
										obligatorios. Asegúrese de proporcionar información precisa
										para todos los huéspedes.
									</p>
								</div>
							</div>
						</div>
					</div>

					<!-- Notifications Section (Hidden for now) -->
					<div class="hidden space-y-6">
						<div class="flex items-center gap-3 pb-4 border-b border-gray-100">
							<div
								class="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center"
							>
								<svg
									class="w-4 h-4 text-purple-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M15 17h5l-5 5v-5zM12 8a2 2 0 100-4 2 2 0 000 4zm-7.5 1L1 12l3.5 3L1 18l3.5 3m13-6l3.5-3L18 9l3.5-3"
									></path>
								</svg>
							</div>
							<div>
								<h4 class="font-inter font-semibold text-gray-900">
									Notificaciones
								</h4>
								<p class="font-inter text-sm text-gray-600">
									Configure las preferencias de comunicación
								</p>
							</div>
						</div>

						<div class="space-y-2">
							<label class="block font-inter text-sm font-medium text-gray-700">
								Correo electrónico del viajero
								<span class="text-red-500">*</span>
							</label>
							<p class="font-inter text-xs text-gray-500">
								Los cambios en este campo no se reflejarán en el perfil del
								viajero
							</p>
							<input
								type="email"
								class="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-900 font-inter focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
								placeholder="correo@ejemplo.com"
							/>
							<span
								v-if="
									attemptedToSubmit &&
									form.travelers[travelerIndex].email === ''
								"
								class="text-sm text-red-500 font-inter"
							>
								{{
									validate(
										form.travelers[travelerIndex].email,
										inputTypes.email
									)
								}}
							</span>
						</div>
					</div>
				</form>
			</div>
		</div>
	</div>
</template>

<style scoped>
	.font-inter {
		font-family:
			'Inter',
			system-ui,
			-apple-system,
			sans-serif;
	}

	/* Custom focus styles */
	input:focus {
		box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
	}

	/* Smooth transitions */
	* {
		transition-property:
			background-color, border-color, color, fill, stroke, opacity, box-shadow,
			transform;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 200ms;
	}
</style>
